import { Tool } from '@procureai/agents';
import { isNotEmpty, isNotNil, keys } from 'ramda';
import { Trans } from 'react-i18next';

import { ChatLoadingIndicator } from '../ChatMessage/indicators/ChatLoadingIndicator';
import { ChatToolErrorIndicator } from '../ChatMessage/indicators/ChatToolErrorIndicator';
import { ChatToolResultIndicator } from '../ChatMessage/indicators/ChatToolResultIndicator';
import { I18nNamespace } from '@/legacy/common/types';
import { TransCuriosityLookup } from '@/legacy/modules/BusinessLogWidget/components/BusinessLogItem/TransCuriosityLookup/TransCuriosityLookup';

import { ToolResultComponent } from '.';

const checkOutput = (output: Record<string, unknown> | undefined | null): output is Record<string, unknown> => {
  return isNotNil(output) && isNotEmpty(output);
};

type ToolStatus = 'call' | 'result' | 'error';

const getToolStatus = ({ state, output }: { state: string; output?: any }): ToolStatus => {
  switch (state) {
    case 'input-streaming':
    case 'input-available':
      return 'call';

    case 'output-available':
      return checkOutput(output) ? 'result' : 'error';
    default:
      return 'error';
  }
};

const IndicatorVariant: { [key in ToolStatus]: React.ComponentType<any> } = {
  call: ChatLoadingIndicator,
  result: ChatToolResultIndicator,
  error: ChatToolErrorIndicator,
};

/**
 *
 * @param status - actual status variant loaded from logs
 * @param fieldIds - list of file ids lookup up by curiosity
 * @returns
 */
const ReadAttachmentStatusTranslation = ({
  status,
  fileIds,
}: {
  status: ToolStatus;
  fileIds: string[] | undefined;
}) => {
  return (
    <Trans
      ns={I18nNamespace.intake}
      i18nKey={`Chat.tools.${Tool.runReadAttachments}.${status}`}
      values={fileIds}
      components={{
        // using zero="" to avoid rendering anything when fileIds is empty
        // comes in handy on errors messages, where input is not available
        // and falls back to an empty array therefore
        lookup: <TransCuriosityLookup context={{ fileIds }} zero="" />,
      }}
    />
  );
};

export const RunReadAttachments: ToolResultComponent<
  Tool.runReadAttachments,
  { fileIds: string[] },
  Record<string, unknown>
> = ({ tool }) => {
  const status = getToolStatus(tool);
  // picks either output fileIds (on success) or input fileIds while streaming
  // on error an empty array is used as input isn't available there
  const fileIds = status === 'result' && checkOutput(tool.output) ? keys(tool.output) : (tool.input?.fileIds ?? []);

  const IndicatorComponent = IndicatorVariant[status];

  return <IndicatorComponent message={<ReadAttachmentStatusTranslation status={status} fileIds={fileIds} />} />;
};
